import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExIcon,
    ExInput,
    SearchFlavor,
    SearchSize,
} from '@boomi/exosphere';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import AnimatedLink from '../common/AnimatedLink';
import { useApp } from '../common/AppProvider';
import IconCircle from '../common/IconCircle';
import { useMobile } from '../hooks/useMobile';
import { translations } from '../translations/translations';
import type { AutomationTask } from '../types/types';

import './filters.css';

const hasMatchingStatus = (filteringStatus: string | null, task: AutomationTask): boolean =>
    (filteringStatus === 'active' && task.isActive) ||
    (filteringStatus === 'inactive' && !task.isActive) ||
    filteringStatus === null;

type SortValue =
    | 'CREATED_NEW'
    | 'CREATED_OLD'
    | 'UPDATED_NEW'
    | 'UPDATED_OLD'
    | 'A_TO_Z'
    | 'Z_TO_A';

type SortOption = {
    value: SortValue;
    label: string;
};

const TasksFilters = () => {
    const { tasks, connectors, language, setFilteredTasks } = useApp();
    const { isMobile } = useMobile(800);
    const sortOptions: SortOption[] = [
        { value: 'UPDATED_NEW', label: translations[language].SORT_updated_newest },
        { value: 'UPDATED_OLD', label: translations[language].SORT_updated_oldest },
        { value: 'CREATED_NEW', label: translations[language].SORT_created_newest },
        { value: 'CREATED_OLD', label: translations[language].SORT_created_oldest },
        { value: 'A_TO_Z', label: translations[language].SORT_name_atoz },
        { value: 'Z_TO_A', label: translations[language].SORT_name_ztoa },
    ];
    const orderByStorageKey = 'sessionOrderBy';
    const getOrderByFromStorage = () =>
        sortOptions.find((option) => option.value === sessionStorage.getItem(orderByStorageKey));
    const [availableConnectors, setAvailableConnectors] = useState<string[]>([]);
    const [filteringConnectors, setFilteringConnectors] = useState<string[]>([]);
    const [filteringStatus, setFilteringStatus] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [orderBy, setOrderBy] = useState<SortOption>(getOrderByFromStorage() ?? sortOptions[0]);
    const filterWrapper = useRef<HTMLDivElement>(null);
    const filterContainerElement = document.querySelector<HTMLElement>('.automate-root');
    const noOfAppliedFilters = filteringConnectors.length + (filteringStatus ? 1 : 0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const tasksConnectors: string[] = [];

        tasks.forEach((task) => {
            if (task.trigger) {
                const triggerRef = task.trigger.providerId;

                if (triggerRef && !tasksConnectors.includes(triggerRef)) {
                    tasksConnectors.push(triggerRef);
                }

                task.actions.forEach((action) => {
                    const actionRef = action.providerId;

                    if (actionRef && !tasksConnectors.includes(actionRef)) {
                        tasksConnectors.push(actionRef);
                    }
                });
            }
        });

        setAvailableConnectors(tasksConnectors);

        // remove any active filters for connectors that are no longer available
        if (filteringConnectors.length) {
            const availableFilteringConnectors = filteringConnectors.filter((connector) =>
                tasksConnectors.includes(connector),
            );

            if (availableFilteringConnectors.length !== filteringConnectors.length) {
                setFilteringConnectors(availableFilteringConnectors);
            }
        }
    }, [tasks, connectors]);

    useEffect(() => {
        // apply sort
        let sortedTasks: AutomationTask[];

        switch (orderBy.value) {
            case 'CREATED_NEW':
                sortedTasks = [...tasks]
                    .sort((a, b) => Date.parse(a.createdAt!) - Date.parse(b.createdAt!))
                    .reverse();
                break;
            case 'CREATED_OLD':
                sortedTasks = [...tasks].sort(
                    (a, b) => Date.parse(a.createdAt!) - Date.parse(b.createdAt!),
                );
                break;
            case 'UPDATED_NEW':
                sortedTasks = [...tasks]
                    .sort((a, b) => Date.parse(a.updatedAt!) - Date.parse(b.updatedAt!))
                    .reverse();
                break;
            case 'UPDATED_OLD':
                sortedTasks = [...tasks].sort(
                    (a, b) => Date.parse(a.updatedAt!) - Date.parse(b.updatedAt!),
                );
                break;
            case 'A_TO_Z':
                sortedTasks = [...tasks].sort((a, b) => a.displayName.localeCompare(b.displayName));
                break;
            case 'Z_TO_A':
                sortedTasks = [...tasks].sort((a, b) => b.displayName.localeCompare(a.displayName));
                break;
            default:
                sortedTasks = [...tasks];
        }

        // if no filters or search just use the sorting and exit here
        if (search === '' && filteringConnectors.length === 0 && filteringStatus === null) {
            setFilteredTasks([...sortedTasks]);
            return;
        }

        // apply filters
        const filteredTasks = sortedTasks.filter((task) => {
            if (task.trigger) {
                if (filteringConnectors.includes(task.trigger.providerId)) {
                    if (hasMatchingStatus(filteringStatus, task)) {
                        return true;
                    }
                }

                for (const action of task.actions) {
                    if (filteringConnectors.includes(action.providerId)) {
                        if (hasMatchingStatus(filteringStatus, task)) {
                            return true;
                        }
                    }
                }

                if (filteringConnectors.length === 0 && hasMatchingStatus(filteringStatus, task)) {
                    return true;
                }
            }

            return false;
        });

        // apply search
        const filteredAndSearchedTasks = filteredTasks.filter((task) =>
            task.displayName.toUpperCase().includes(search.toUpperCase()),
        );

        setFilteredTasks(filteredAndSearchedTasks);
    }, [filteringConnectors, tasks, setFilteredTasks, filteringStatus, search, orderBy]);

    const onToggleFilter = (connectorId: string) => {
        const filterList = [...filteringConnectors];

        if (filterList.includes(connectorId)) {
            const filtered = filterList.filter((c) => c !== connectorId);
            setFilteringConnectors(filtered);
        } else {
            filterList.push(connectorId);
            setFilteringConnectors(filterList);
        }
    };

    const onToggleFiltersPanel = () => {
        filterWrapper?.current?.classList.toggle('is-active');
    };

    const renderFiltersPanel = () => (
        <div className="filter-panel-layout">
            <header className="filter-header">
                <h3 className="title lg">{translations[language].FILTER_filter}</h3>
                <ExButton
                    data-testid="filtersClearButton"
                    className={`filter-clear exo-button-action ${!noOfAppliedFilters ? 'is-hidden' : ''}`}
                    flavor={ButtonFlavor.BRANDED}
                    size={ButtonSize.LARGE}
                    type={ButtonType.TERTIARY}
                    onClick={() => {
                        setFilteringConnectors([]);
                        setFilteringStatus(null);
                    }}
                    title={translations[language].FILTER_filter_clear_all}
                >
                    <ExIcon icon="Undo" hide-browser-tooltip />
                </ExButton>
                <ExButton
                    className="filter-close exo-button-action"
                    flavor={ButtonFlavor.BRANDED}
                    size={ButtonSize.LARGE}
                    type={ButtonType.TERTIARY}
                    onClick={onToggleFiltersPanel}
                    title={translations[language].COMMON_close}
                >
                    <ExIcon icon="Close" hide-browser-tooltip />
                </ExButton>
            </header>

            {/* Filter by connectors */}
            <section className="filter-section">
                <span className="label md">
                    {translations[language].FILTER_filter_by_connector}:
                </span>
                <div className="filter-section-content">
                    {availableConnectors
                        .sort((a, b) => a.localeCompare(b))
                        .map((connectorId) => (
                            <button
                                className={
                                    filteringConnectors.includes(connectorId)
                                        ? 'app-tile is-selected'
                                        : 'app-tile'
                                }
                                key={connectorId}
                                onClick={() => onToggleFilter(connectorId)}
                                title={connectors[connectorId].displayName}
                                type="button"
                            >
                                <IconCircle connector={connectors[connectorId]} adaptiveSize />
                                <span className="app-tile-text">
                                    {connectors[connectorId].displayName}
                                </span>
                            </button>
                        ))}
                </div>
            </section>

            {/* Filter by status */}
            <section className="filter-section">
                <span className="label md">{translations[language].FILTER_filter_by_status}:</span>
                <div className="filter-section-content select-chips">
                    <label className="filter-status-label text sm" htmlFor="active">
                        <input
                            type="checkbox"
                            id="active"
                            name="task_status"
                            value="active"
                            onChange={() => {
                                const status = filteringStatus === 'active' ? null : 'active';
                                setFilteringStatus(status);
                            }}
                            checked={filteringStatus === 'active'}
                        />
                        {translations[language].FILTER_filter_status_active}
                    </label>
                    <label className="filter-status-label text sm" htmlFor="inactive">
                        <input
                            type="checkbox"
                            id="inactive"
                            name="task_status"
                            value="inactive"
                            onChange={() => {
                                const status = filteringStatus === 'inactive' ? null : 'inactive';
                                setFilteringStatus(status);
                            }}
                            checked={filteringStatus === 'inactive'}
                        />
                        {translations[language].FILTER_filter_status_inactive}
                    </label>
                </div>
            </section>
        </div>
    );

    return (
        <>
            <section className="filter-bar">
                {/* SEARCH */}
                <ExInput
                    data-testId="filterSearchInput"
                    className="exo-search-input"
                    placeholder={translations[language].COMMON_search_placeholder}
                    input-id="default"
                    type="text"
                    inputmode="text"
                    size={SearchSize.MEDIUM}
                    flavor={SearchFlavor.WHITE}
                    leadingIcon="magnifying-glass"
                    onChange={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
                    disabled={!tasks.length}
                    autoComplete="off"
                    clearable
                />

                {/* ORDER BY */}
                <Select
                    className="react-select-container filter-sort"
                    classNamePrefix="react-select"
                    options={sortOptions}
                    onChange={(selected) => {
                        setOrderBy(selected as SortOption);
                        sessionStorage.setItem(orderByStorageKey, (selected as SortOption).value);
                    }}
                    value={{
                        value: orderBy.value,
                        label: `${translations[language].SORT_prefix} ${orderBy.label}`,
                    }}
                    isMulti={false}
                    isClearable={false}
                    isSearchable={false}
                    menuPlacement="auto"
                />

                {/* FILTER TRIGGER */}
                <ExButton
                    data-testId="filterTriggerButton"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.LARGE}
                    type={ButtonType.SECONDARY}
                    onClick={onToggleFiltersPanel}
                    className="exo-button filter-trigger-button"
                    disabled={!tasks.length}
                >
                    <ExIcon className="filter-trigger-icon" icon="filter" hide-browser-tooltip />
                    {translations[language].FILTER_filter}
                    {noOfAppliedFilters > 0 && (
                        <span className="filter-trigger-counter">{noOfAppliedFilters}</span>
                    )}
                </ExButton>

                {/* NEW TASK */}
                <AnimatedLink className="filter-new-task-button" to="/tasks/new" tabIndex={-1}>
                    <ExButton
                        className="exo-button"
                        flavor={ButtonFlavor.BASE}
                        size={ButtonSize.LARGE}
                        type={ButtonType.PRIMARY}
                    >
                        <ExIcon
                            className="new-task-icon"
                            icon="plus"
                            slot="prefix"
                            hide-browser-tooltip
                        />
                        {translations[language].TASKS_add_new}
                    </ExButton>
                </AnimatedLink>
            </section>

            {isMobile && filterContainerElement ? (
                createPortal(
                    <div
                        className="filter-panel filter-panel-mobile"
                        ref={filterWrapper}
                        data-testid="tasksFiltersPanel"
                    >
                        {renderFiltersPanel()}
                    </div>,
                    filterContainerElement,
                )
            ) : (
                <div
                    className="filter-panel filter-panel-desktop"
                    ref={filterWrapper}
                    data-testid="tasksFiltersPanel"
                >
                    {renderFiltersPanel()}
                </div>
            )}
        </>
    );
};

export default TasksFilters;
